import React from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import { SxProps } from '@mui/material/styles'

import { FilterAlt } from '@mxenabled/mx-icons'

import { useAccountStore, useGlobalCopyStore, useGlobalUiStore } from '../context'
import { AccountType } from '../../core'
import { buildDynamicCopy } from '../../core/utils/Localization'
import { useAriaLive } from '../hooks/useAriaLive'

import { GlobalAccountFilterList } from './globalaccountfilter/GlobalAccountFilterList'
import { buildSelectionFilter, getSelectionCounts } from './globalaccountfilter/selection'

export interface GlobalAccountFilterProps {
  allowedAccountTypes?: AccountType[] | undefined
  isDesktop?: boolean
  onAccountFilterClick?: () => void
  sx?: SxProps
  variant?: 'outlined' | 'text'
}

export const GlobalAccountFilter = observer(
  ({
    allowedAccountTypes,
    isDesktop,
    onAccountFilterClick,
    sx = {},
    variant = 'text',
  }: GlobalAccountFilterProps) => {
    const { common: commonCopy } = useGlobalCopyStore()
    const { accountFilterMap, resetAccountFilterMap } = useGlobalUiStore()

    const { detailedAccounts, updateAccountFilter } = useAccountStore()

    // Two regions, because MUI's ModalManager sets aria-hidden on every body
    // child outside the popover while it is open (and aria-modal tells AT to
    // ignore them anyway). Selection changes happen while the dialog is open, so
    // that region must live INSIDE it; the applied message is spoken after the
    // dialog is gone, so its region must live outside to survive the unmount.
    const { announce: announceSelection, ariaLive: selectionAriaLive } = useAriaLive()
    const { announce: announceApplied, ariaLive: appliedAriaLive } = useAriaLive()

    const [filterButtonEl, setFilterButtonEl] = React.useState<HTMLButtonElement | null>(null)
    // Set once the update resolves, consumed by the close effect below. It cannot
    // be announced from handleFilter directly: aria-hidden is still on the rest of
    // the document at that point, so the message would go into a hidden region.
    const pendingAppliedRef = React.useRef<string | null>(null)
    // Identifies one open..close cycle of the dialog. `handleFilter` captures it
    // before awaiting and bails if it changed, so a slow request cannot reach
    // back into a session the user has already left.
    const generationRef = React.useRef(0)

    const copy = commonCopy.account_filter

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
      generationRef.current += 1
      // Not subsumed by the generation guard. handleOpen and handleCancel both
      // bump the generation, so a single interrupted apply bails before it writes.
      // What still reaches this line is a SECOND apply started while the first was
      // in flight -- applying does not bump, so both continuations pass the guard.
      // The first closes the dialog and the effect consumes its message; the second
      // then writes onto an already-null anchor, so setFilterButtonEl(null) is a
      // no-op, React bails out of the render, and the effect never runs to consume
      // it. Clearing on the way back in stops that leftover surfacing later.
      //
      // Pinned by 'discards a leftover message from a second apply on the next
      // open' -- delete this line and that test fails. An in-flight guard on apply
      // (MEW-4138) would remove the reachable case and retire both.
      pendingAppliedRef.current = null
      resetAccountFilterMap()
      setFilterButtonEl(event.currentTarget)
      onAccountFilterClick?.()
    }

    const handleCancel = () => {
      // Ends the session too, so a request abandoned here cannot write its message
      // even if it resolves before the user reopens.
      generationRef.current += 1
      setFilterButtonEl(null)
    }

    const handleFilter = async () => {
      const { selected, total } = getSelectionCounts(
        buildSelectionFilter(accountFilterMap, detailedAccounts, allowedAccountTypes),
      )

      const generation = generationRef.current

      await updateAccountFilter(accountFilterMap)

      // The dialog moved on while this was in flight -- the user cancelled, or
      // cancelled and reopened. Announcing now would speak over a session that did
      // not ask for it, and closing would shut a dialog the user just opened.
      // NOTE: the update itself already committed; cancelling does not roll it
      // back. Only the announcement and the close are suppressed here.
      if (generation !== generationRef.current) return

      pendingAppliedRef.current = buildDynamicCopy(
        copy.applied_announcement,
        String(selected),
        String(total),
      ) as string
      setFilterButtonEl(null)
    }

    // Bound to the Paper, not an inner element: FocusTrap focuses the Paper on
    // open (getFocusTarget finds no `data-mui-focusable` marker and falls back to
    // the root), and keydown there bubbles UP -- a handler on a descendant would
    // never see the first keypress.
    //
    // Only fires when the Paper ITSELF holds focus. Anything focused inside it
    // owns its own Enter behaviour: buttons activate, and a checkbox must not
    // submit -- native checkboxes ignore Enter, so without this the keypress
    // bubbled up here and closed the dialog out from under someone who was just
    // moving through the list. A whitelist rather than a `closest()` exclusion
    // list, so a control added later cannot silently inherit submit-on-Enter.
    //
    // Narrower than it looks: the Paper is tabIndex="-1", so once focus moves off
    // it there is no way to Tab back. Enter-applies is a one-shot affordance,
    // available only before the first Tab. The scroll region (tabIndex={0}) is the
    // first stop after it and deliberately does nothing on Enter.
    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key !== 'Enter') return
      if (event.target !== event.currentTarget) return

      handleFilter()
    }

    const isFilterOpen = Boolean(filterButtonEl)
    const filterDisabled = detailedAccounts.length === 0

    // Announce only once the popover is closed. `handleFilter` cannot announce
    // directly: at that point ModalManager still has aria-hidden on the rest of
    // the document, so the message would be written into a hidden region.
    //
    // Modal lifts aria-hidden in an effect when `open` flips false
    // (`closeAfterTransition` defaults false, so `useModal`'s open/close effect
    // calls handleClose immediately rather than waiting for the exit). Modal is a
    // descendant of this component and React flushes child effects first (and every
    // destroy before any create, so FocusTrap's restore lands first too), meaning
    // by the time this effect runs focus is back on the trigger and the document is
    // exposed again.
    //
    // Only ever consumes a message from the session that is closing. An apply
    // interrupted by a cancel or a reopen is stopped at the generation guard in
    // handleFilter and never writes one, in either order.
    React.useEffect(() => {
      if (isFilterOpen || !pendingAppliedRef.current) return

      announceApplied(pendingAppliedRef.current)
      pendingAppliedRef.current = null
    }, [isFilterOpen, announceApplied])

    return (
      <Box sx={{ ...sx }}>
        {appliedAriaLive}
        {isDesktop ? (
          <Button
            aria-controls={isFilterOpen ? 'filter_accounts' : undefined}
            aria-expanded={isFilterOpen}
            aria-haspopup="dialog"
            aria-label={copy.title}
            disabled={filterDisabled}
            onClick={handleOpen}
            startIcon={<FilterAlt size={24} />}
            sx={{ p: 8, whiteSpace: 'nowrap', minWidth: 'max-content' }}
            variant={variant}
          >
            {commonCopy.filter_button}
          </Button>
        ) : (
          <Tooltip id="filter-accounts" placement="bottom" title={copy.title}>
            <IconButton
              aria-controls={isFilterOpen ? 'filter_accounts' : undefined}
              aria-expanded={isFilterOpen}
              aria-haspopup="dialog"
              aria-label={copy.title}
              color="primary"
              disabled={filterDisabled}
              onClick={handleOpen}
              sx={{ p: 8 }}
            >
              <FilterAlt size={24} />
            </IconButton>
          </Tooltip>
        )}

        <Popover
          anchorEl={filterButtonEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          onClose={handleCancel}
          open={isFilterOpen}
          // aria-modal is what Chrome surfaces to the Android accessibility API as a
          // modal subtree. Without it TalkBack walks past the last control and out of
          // the webview into the host app's native chrome -- aria-hidden (applied to
          // background siblings by MUI's Modal) only bounds the DOM, not the webview.
          slotProps={{
            paper: {
              'aria-label': copy.title,
              'aria-modal': true,
              onKeyDown: handleKeyDown,
              role: 'dialog',
            },
          }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Stack>
            {selectionAriaLive}
            <GlobalAccountFilterList
              allowedAccountTypes={allowedAccountTypes}
              announce={announceSelection}
            />
            <Divider />
            <Stack
              direction="row"
              justifyContent="space-between"
              spacing={16}
              sx={{ bgcolor: 'background.paper', bottom: 0, p: 16, position: 'sticky' }}
            >
              <Button fullWidth={true} onClick={handleCancel} variant="text">
                {commonCopy.cancel_button}
              </Button>
              <Button color="primary" fullWidth={true} onClick={handleFilter} variant="contained">
                {commonCopy.filter_button}
              </Button>
            </Stack>
          </Stack>
        </Popover>
      </Box>
    )
  },
)
